import { createAsyncThunk } from '@reduxjs/toolkit';
import { fetchApiAuth } from '../utils/api';
import { markFeatureTourSeen } from '../user/userSlice';
import { CombinedStates } from '../types';

/**
 * Record that this user has finished or dismissed the guided feature tour.
 *
 * The local flag is flipped immediately (before the request resolves) so the
 * tour cannot re-open mid-session, and it is deliberately left flipped if the
 * request fails: showing the tour again is more annoying than losing the record,
 * and the user would simply get it once more at their next login.
 *
 * The `condition` guard is what makes the double-call safe — antd fires onClose
 * alongside onFinish on the final step, and both funnel through here.
 */
export const completeFeatureTour = createAsyncThunk<void, void, { state: CombinedStates }>(
    'featureTour/complete',
    async (_arg, { dispatch }) => {
        dispatch(markFeatureTourSeen());

        try {
            await fetchApiAuth({
                method: 'POST',
                url: 'user/feature-tour/complete',
            });
        } catch {
            // Silent by design: the tour is already gone for this user, and a
            // failed write only costs them seeing it once more next login. No
            // error toast — nothing here is worth interrupting them for.
        }
    },
    {
        condition: (_arg, { getState }) => {
            const state = getState();
            return !state.user.userData?.hasSeenFeatureTour && !state.featureTour.isMarkingSeen;
        },
    },
);

export default completeFeatureTour;
